import { ChangeEvent, useEffect, useRef, useState } from "react";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { UStyles } from "@/app/utils/UStyles";
import defaultInputStyles from "../../styles/DefaultInput.module.css";
import styles from "./Input.module.css";

export enum TextInputType {
  TEXT = "text",
  TEXTAREA = "textarea",
  EMAIL = "email",
  PASSWORD = "password",
  URL = "url",
  NUMBER = "number",
  TEL = "tel",
  DATE = "date",
}

export type InputProps = {
  type?: TextInputType;
  label?: string;
  labelSuffix?: React.ReactNode;
  lang?: string;
  required?: boolean;
  showRequiredLabel?: boolean;
  placeholder?: string;
  data: any;
  dataKey?: string;
  errorMessage?: string;
  charsLimit?: {
    min?: number;
    max?: number;
    showCounter?: boolean;
  };
  textareaRows?: number;
  textareaMaxHeight?: string;
  onClick?: (data: any) => void;
  onChange?: (data: any) => void;
  autoFocus?: boolean;
  hideZero?: boolean;
  disabled?: boolean;
};

export function Input(props: InputProps) {
  let {
    type = TextInputType.TEXT,
    label,
    labelSuffix,
    lang,
    required,
    showRequiredLabel,
    placeholder = "",
    data = {},
    dataKey = "",
    errorMessage = "",
    charsLimit,
    textareaRows = 3,
    textareaMaxHeight,
    onClick = () => {},
    onChange,
    autoFocus,
    hideZero,
    disabled = false,
  } = props;

  if (!dataKey) {
    const [key] = Object.entries(data)[0];
    dataKey = key;
  }

  const [valueLen, setValueLen] = useState((data[dataKey] || "").length);

  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea: any = textareaRef.current;
    if (textarea) {
      if (autoFocus) {
        textarea.focus();
      }
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [autoFocus]);

  const handleChange = (e: ChangeEvent) => {
    if (e.target) {
      const input: HTMLInputElement = e.target as HTMLInputElement;

      if (type === TextInputType.NUMBER) {
        data[dataKey] = parseInt(input.value || "0", 10);
        if (hideZero && data[dataKey] === 0) {
          input.value = "";
        } else {
          input.value = data[dataKey];
        }
      } else {
        data[dataKey] = input.value || "";
      }

      if (onChange) {
        onChange(data);
      }

      if (charsLimit?.showCounter) {
        setValueLen(data[dataKey].length);
      }
    }
  };

  const buildInput = () => {
    switch (type) {
      case TextInputType.TEXTAREA:
        return (
          <textarea
            ref={textareaRef}
            disabled={disabled}
            className={defaultInputStyles.input}
            name={dataKey}
            minLength={charsLimit?.min}
            maxLength={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={data[dataKey]}
            onClick={onClick}
            onChange={handleChange}
            onInput={(e: any) => {
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
              handleChange(e);
            }}
            style={{
              overflow: textareaMaxHeight ? "auto" : "hidden",
              resize: "none",
              maxHeight: textareaMaxHeight,
            }}
            rows={textareaRows}
          />
        );

      case TextInputType.URL:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={type}
            pattern={"https://.*"}
            name={dataKey}
            minLength={charsLimit?.min}
            maxLength={charsLimit?.max}
            placeholder={placeholder ? placeholder : "https://..."}
            defaultValue={data[dataKey]}
            onClick={onClick}
            onChange={handleChange}
          />
        );

      case TextInputType.NUMBER:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={type}
            name={dataKey}
            min={charsLimit?.min}
            max={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={data[dataKey]}
            onClick={onClick}
            onChange={handleChange}
          />
        );

      case TextInputType.TEL:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={"number"}
            name={dataKey}
            min={charsLimit?.min}
            max={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={data[dataKey]}
            onClick={onClick}
            onChange={handleChange}
          />
        );
    }

    return (
      <input
        disabled={disabled}
        className={defaultInputStyles.input}
        type={type}
        name={dataKey}
        minLength={charsLimit?.min}
        maxLength={charsLimit?.max}
        placeholder={placeholder}
        defaultValue={data[dataKey]}
        onClick={onClick}
        onChange={handleChange}
      />
    );
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {showRequiredLabel &&
            (required ? (
              <span className={styles.labelRequired}>*</span>
            ) : (
              <span className={styles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
          {labelSuffix && <span className={styles.labelSuffix}>{labelSuffix}</span>}
        </label>
      )}
      {buildInput()}

      <div className={styles.content}>
        {errorMessage ? (
          <div className={UStyles.classNames(styles.errorMessage, defaultInputStyles.errorMessage)}>
            <ErrorMessage message={errorMessage} />
          </div>
        ) : (
          <div></div>
        )}

        {charsLimit?.showCounter && (
          <div className={styles.charsLimit}>
            {charsLimit.showCounter && (
              <p className={styles.charsLimitText}>
                {valueLen}/{charsLimit.max}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
